import { useState } from "react";
import * as XLSX from "xlsx";
import api, { apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileArrowDown, FileArrowUp, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/** Column keys that map to the LoanIn model. Order = template columns. */
const TEMPLATE_COLS = [
  { key: "collateral",             label: "Collateral" },
  { key: "loan_type_class",        label: "Loan Type" },
  { key: "lender",                 label: "Lender" },
  { key: "borrower",               label: "Borrower" },
  { key: "principal",              label: "Loan Principal" },
  { key: "monthly_payment",        label: "Monthly Payment" },
  { key: "construction_holdback",  label: "Construction Holdback Amt" },
  { key: "origination_points",     label: "Origination Points" },
  { key: "extension_points",       label: "Extension Points" },
  { key: "release_amount",         label: "Release Amount" },
  { key: "sale_amount",            label: "Sale Price" },
  { key: "purchase_price",         label: "Purchase Price" },
  { key: "current_value",          label: "Curr. Value" },
  { key: "arv",                    label: "ARV" },
  { key: "term_months",            label: "Term (mo)" },
  { key: "rate_type",              label: "Type" },
  { key: "loan_start_date",        label: "Loan Start Date" },
  { key: "maturity_date",          label: "Maturity Date" },
  { key: "interest_rate",          label: "Interest Rate" },
  { key: "step_up_date",           label: "Step Up Date" },
  { key: "step_up_rate",           label: "Step Up Rate" },
  { key: "interest_reserve",       label: "Interest Reserve" },
  { key: "balloon_payment",        label: "Balloon Payment" },
  { key: "monthly_revenue",        label: "Monthly Revenue" },
  { key: "monthly_expense",        label: "Monthly Expense" },
  { key: "monthly_tax",            label: "Monthly Tax" },
];

const NUMERIC_KEYS = new Set([
  "principal", "monthly_payment", "construction_holdback",
  "origination_points", "extension_points", "release_amount", "sale_amount", "purchase_price",
  "current_value", "arv", "term_months",
  "interest_rate", "step_up_rate", "interest_reserve", "balloon_payment",
  "monthly_revenue", "monthly_expense", "monthly_tax",
]);
const DATE_KEYS = new Set(["maturity_date", "step_up_date", "loan_start_date"]);

export default function BulkUploadModal({ open, onClose, onImported }) {
  const [rows, setRows] = useState(null);       // parsed rows preview
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const header = TEMPLATE_COLS.map((c) => c.label);
    // A helpful example row so the user sees the expected shape.
    const example = TEMPLATE_COLS.map((c) => {
      if (c.key === "collateral") return "1 Carnation Ave Floral Park NY 11001";
      if (c.key === "loan_type_class") return "1st Lien Mortgage";
      if (c.key === "lender") return "Jerry Heller";
      if (c.key === "borrower") return "NY Rentals LLC";
      if (c.key === "principal") return 400000;
      if (c.key === "monthly_payment") return 3000;
      if (c.key === "interest_rate") return 8.5;
      if (c.key === "maturity_date") return "2026-04-15";
      if (c.key === "rate_type") return "fixed";
      return "";
    });
    const ws = XLSX.utils.aoa_to_sheet([header, example]);
    ws["!cols"] = TEMPLATE_COLS.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, ws, "Assets Template");
    XLSX.writeFile(wb, "STRATA ASSETS TEMPLATE.xlsx");
  };

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const arr = XLSX.utils.sheet_to_json(ws, { defval: "" });
        // Map each row from label → key.
        const labelToKey = new Map(TEMPLATE_COLS.map((c) => [c.label.toLowerCase(), c.key]));
        const parsed = arr.map((r) => {
          const out = {};
          for (const [k, v] of Object.entries(r)) {
            const norm = String(k).trim().toLowerCase();
            const target = labelToKey.get(norm);
            if (!target) continue;
            if (v === "" || v == null) continue;
            if (NUMERIC_KEYS.has(target)) {
              const n = Number(v);
              if (!isNaN(n)) out[target] = n;
            } else if (DATE_KEYS.has(target)) {
              if (v instanceof Date) {
                out[target] = v.toISOString().slice(0, 10);
              } else {
                const d = new Date(v);
                if (!isNaN(d)) out[target] = d.toISOString().slice(0, 10);
                else out[target] = String(v).slice(0, 10);
              }
            } else {
              out[target] = String(v).trim();
            }
          }
          return out;
        }).filter((r) => Object.keys(r).length > 0);
        setRows(parsed);
        setResult(null);
      } catch (err) {
        toast.error(`Could not read that file: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const submit = async () => {
    if (!rows?.length) return;
    setUploading(true);
    try {
      const { data } = await api.post("/loans/bulk-import", { rows });
      setResult(data);
      toast.success(`Imported ${data.created} new asset${data.created === 1 ? "" : "s"}; skipped ${data.skipped}.`);
      onImported && onImported();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="bulk-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileArrowUp size={18} className="text-[#327de3]" /> Bulk upload assets/loans
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
            <strong>How to use:</strong>
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Download the template.</li>
              <li>Fill in one row per asset — the column headers must not be changed.</li>
              <li>Upload the completed file. Any row whose Collateral + Lender + Borrower + Principal + Maturity match an existing asset is skipped automatically to prevent duplicates.</li>
            </ol>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadTemplate} data-testid="bulk-download-template">
              <FileArrowDown size={14} className="mr-1.5" /> Download template
            </Button>
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
              <FileArrowUp size={14} /> Choose file
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) parseFile(f); e.target.value = ""; }} data-testid="bulk-file-input" />
            </label>
            {rows && <span className="text-xs text-slate-500">Parsed {rows.length} row{rows.length === 1 ? "" : "s"} — ready to import.</span>}
          </div>

          {rows && rows.length > 0 && (
            <div className="rounded-md border border-slate-200 overflow-hidden">
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      {TEMPLATE_COLS.slice(0, 8).map((c) => <th key={c.key} className="text-left px-2 py-1 border-b border-slate-200 font-semibold text-slate-600">{c.label}</th>)}
                      <th className="text-left px-2 py-1 border-b border-slate-200 font-semibold text-slate-600">…</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 25).map((r, i) => (
                      <tr key={i} className="odd:bg-white even:bg-slate-50/40">
                        {TEMPLATE_COLS.slice(0, 8).map((c) => <td key={c.key} className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap">{r[c.key] ?? ""}</td>)}
                        <td className="px-2 py-0.5 border-b border-slate-100 text-slate-400">…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 25 && <div className="text-[10px] text-slate-500 italic px-2 py-1">+ {rows.length - 25} more row(s) — will be included.</div>}
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-md border border-slate-200 p-3 text-xs" data-testid="bulk-result">
              <div className="flex items-center gap-4 mb-2">
                <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle size={14} /> Created: {result.created}</span>
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><WarningCircle size={14} /> Skipped: {result.skipped}</span>
              </div>
              {result.skipped_items?.length > 0 && (
                <details>
                  <summary className="text-slate-500 cursor-pointer text-[11px]">Show skipped rows and reasons</summary>
                  <ul className="mt-1 space-y-0.5">
                    {result.skipped_items.slice(0, 20).map((s, i) => (
                      <li key={i} className="text-[10px] text-slate-600">
                        <strong>{s.reason}</strong> — {s.row?.collateral || "(no collateral)"} {s.row?.lender ? `· ${s.row.lender}` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={!rows?.length || uploading} className="bg-[#327de3] hover:bg-[#1f64c2]" data-testid="bulk-submit">
            {uploading ? "Importing…" : `Import ${rows?.length || 0} row(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
